import { ApiResult, HttpClient } from "../http.js";
import { PagedTimeEntries, TimeEntryDto, TimeEntryType } from "../types/time-entries.js";

export interface CreateTimeEntryPayload {
  userId: string;
  userName: string;
  // Optional: omit for a quote-anchored (pre-project) entry; `quoteId` then carries the anchor.
  projectId?: string | null;
  projectTitle?: string | null;
  taskId?: string | null;
  taskTitle?: string | null;
  date: string;
  startTime: string;
  endTime?: string | null;
  durationMinutes: number;
  isRunning: boolean;
  type: TimeEntryType;
  billable: boolean;
  description?: string | null;
  hourlyRate?: number | null;
  totalCost?: number | null;
  id?: string;
  createdById?: string | null;
  createdByName?: string | null;
  quoteId?: string | null;
  quoteTitle?: string | null;
  notes?: string | null;
  budgetCategoryId?: string | null;
  locationJson?: string | null;
  /** Validated server-side against the entry user's Løn settings before it is stored. */
  timeTypeId?: string | null;
}

export interface ListTimeEntriesQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  userId?: string;
  projectId?: string;
  quoteId?: string;
  dateFrom?: string;
  dateTo?: string;
  isRunning?: boolean;
  /**
   * Repeated query param (`?timeTypeIds=a&timeTypeIds=b`). Omitted, or empty,
   * means "any timetype" — the two are indistinguishable once serialised, and the
   * server treats them the same.
   */
  timeTypeIds?: readonly string[];
}

export interface UpdateTimeEntryPayload {
  userId: string;
  userName: string;
  // Optional: omit for a quote-anchored (pre-project) entry; `quoteId` then carries the anchor.
  projectId?: string | null;
  projectTitle?: string | null;
  taskId?: string | null;
  taskTitle?: string | null;
  date: string;
  startTime: string;
  endTime?: string | null;
  durationMinutes: number;
  isRunning: boolean;
  type: TimeEntryType;
  billable: boolean;
  description?: string | null;
  hourlyRate?: number | null;
  totalCost?: number | null;
  quoteId?: string | null;
  quoteTitle?: string | null;
  notes?: string | null;
  budgetCategoryId?: string | null;
  locationJson?: string | null;
  /** Validated server-side against the entry user's Løn settings before it is stored. */
  timeTypeId?: string | null;
}

export interface StopTimeEntryPayload {
  endTime: string;
}

export interface MarkTimeEntryInvoicedPayload {
  invoicedInvoiceId: string;
  invoicedFromProjectId: string;
}

export interface ApproveTimeEntryPayload {
  notes?: string | null;
}

export interface RejectTimeEntryPayload {
  reason: string;
}

export class TimeEntriesApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListTimeEntriesQuery = {}): Promise<ApiResult<PagedTimeEntries>> {
    return this.http.get<PagedTimeEntries>("/api/time-entries", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<TimeEntryDto>> {
    return this.http.get<TimeEntryDto>(`/api/time-entries/${encodeURIComponent(id)}`);
  }

  create(payload: CreateTimeEntryPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/time-entries", { body: payload });
  }

  update(id: string, payload: UpdateTimeEntryPayload): Promise<ApiResult<TimeEntryDto>> {
    return this.http.put<TimeEntryDto>(`/api/time-entries/${encodeURIComponent(id)}`, { body: payload });
  }

  stop(id: string, payload: StopTimeEntryPayload): Promise<ApiResult<TimeEntryDto>> {
    return this.http.post<TimeEntryDto>(`/api/time-entries/${encodeURIComponent(id)}/stop`, { body: payload });
  }

  delete(id: string): Promise<ApiResult<void>> {
    return this.http.delete<void>(`/api/time-entries/${encodeURIComponent(id)}`);
  }

  /**
   * Stamp a time entry as invoiced (Opret faktura write-back). Sets only the invoice-tracking
   * fields; a matching-invoice re-mark is an idempotent no-op, any other re-mark is a 409 conflict.
   */
  markInvoiced(id: string, payload: MarkTimeEntryInvoicedPayload): Promise<ApiResult<TimeEntryDto>> {
    return this.http.post<TimeEntryDto>(`/api/time-entries/${encodeURIComponent(id)}/mark-invoiced`, { body: payload });
  }

  // ── Approval workflow ──────────────────────────────────────────────────────

  /** Submit a draft/rejected/revised entry for approval. */
  submit(id: string): Promise<ApiResult<TimeEntryDto>> {
    return this.http.post<TimeEntryDto>(`/api/time-entries/${encodeURIComponent(id)}/submit`, {});
  }

  /** Approve a submitted/revised entry. */
  approve(id: string, payload: ApproveTimeEntryPayload = {}): Promise<ApiResult<TimeEntryDto>> {
    return this.http.post<TimeEntryDto>(`/api/time-entries/${encodeURIComponent(id)}/approve`, { body: payload });
  }

  /** Reject a submitted/revised entry with a required reason. */
  reject(id: string, payload: RejectTimeEntryPayload): Promise<ApiResult<TimeEntryDto>> {
    return this.http.post<TimeEntryDto>(`/api/time-entries/${encodeURIComponent(id)}/reject`, { body: payload });
  }

  /** Revise a rejected entry and resubmit it (→ Revised). Edit fields via `update` first. */
  revise(id: string): Promise<ApiResult<TimeEntryDto>> {
    return this.http.post<TimeEntryDto>(`/api/time-entries/${encodeURIComponent(id)}/revise`, {});
  }

  /** Entries awaiting review (Submitted/Revised) the caller is allowed to approve. */
  pending(): Promise<ApiResult<TimeEntryDto[]>> {
    return this.http.get<TimeEntryDto[]>("/api/time-entries/pending");
  }

  /** The caller's own submitted/approved/rejected entries. */
  mySubmissions(): Promise<ApiResult<TimeEntryDto[]>> {
    return this.http.get<TimeEntryDto[]>("/api/time-entries/my-submissions");
  }
}
